// Native property sections, shared by ordinary cards and scoped form mounts.

import { type RenderCtx, renderCardListBlock, renderSingleItem, renderUserChoiceListRead, renderCardListItemLayerRead, renderUserChoiceReadRow } from './primitives';
import {
    hasLayer, hasTopLevel, topSlot0, layerSlot, layerOf, layerEntriesOf, itemsCol, computedValue, slotString, scalarLayerSlot, layerScoringToken, ucwordsAndSpaceTs, nameOf, referenceNameHtml, sortLayerEntriesByRequirements,
} from './helpers';
import { installPropertyFormMounts, type PropertyChoiceRenderer, type PropertyFormContext } from './property-form-mounts';
import { resolveNest, inventoryField } from '../shared/array-types';
import { SPELL_CASTING_TIME_LABELS, isApCastTime } from '../shared/spell-casting-time';
import { displayDuration, displaySpellLevelBadge } from '../shared/spell-display';

function ucfirst(s: string): string {
    return s === '' ? '' : s.charAt(0).toUpperCase() + s.slice(1);
}

// PHP make_number_pretty: comma thousands; 2 decimals only if a non-zero fractional part.
function makeNumberPretty(x: unknown): string {
    const n = Number(x);
    if (x === null || x === undefined || x === '' || isNaN(n) || !isFinite(n)) return String(x ?? '');
    const hasFraction = Math.abs(n - Math.round(n)) > 0.000001;
    return hasFraction ? n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : Math.round(n).toLocaleString('en-US');
}

type ListDisplayWindow = Record<string, unknown>;

function ldCall(name: string, ...args: unknown[]): unknown {
    const fn = (window as unknown as ListDisplayWindow)[name];
    return typeof fn === 'function' ? fn(...args) : undefined;
}

function ldAttrs(action: string, attrs: Record<string, string> = {}): string {
    const pairs = [`data-ft-ld-action="${escapeHtml(action)}"`];
    for (const [key, value] of Object.entries(attrs)) {
        // HTML lowercases attribute names, so `data-ft-itemId` reads back as dataset.ftItemid,
        // NOT dataset.ftItemId. Kebab-case the key so the camelCase dataset readers resolve.
        pairs.push(`data-ft-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}="${escapeHtml(value)}"`);
    }
    return pairs.join(' ');
}

function ldInfoAttrs(itemId: string): string {
    return ldAttrs('open-info', { itemId });
}

document.addEventListener('click', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-ld-action]')
        : null;
    if (!el) return;
    const action = el.dataset.ftLdAction || '';
    if (action === 'open-info') { event.preventDefault(); ldCall('openItemInfo', el.dataset.ftItemId || ''); return; }
    if (action === 'open-new-tab') { event.preventDefault(); ldCall('openLinkNewTab', el.dataset.ftUrl || ''); return; }
    if (action === 'use-ability') { event.preventDefault(); ldCall('useAbility', el.dataset.ftItemId || ''); return; }
    if (action === 'use-ability-variable1') { event.preventDefault(); ldCall('useAbilityVariable1', el.dataset.ftItemId || '', el.dataset.ftVariable1 || ''); return; }
    if (action === 'roll-dice') {
        event.preventDefault();
        ldCall(
            'rollDice',
            el.dataset.ftRollType || '',
            el.dataset.ftDamageType || '',
            el.dataset.ftItemId || '',
            '',
            el.dataset.ftDice || '',
            el.dataset.ftDie || '',
            el.dataset.ftMod || '0',
            '0',
        );
        return;
    }
    if (action === 'change-inventory-charges') {
        ldCall('changeInventoryCharges', el.dataset.ftInventoryId || '', el.dataset.ftInputId || '');
        return;
    }
    if (action === 'play-soundboard') {
        event.preventDefault();
        ldCall('openSideLog');
        ldCall('clickEl', 'pills-soundboard-tab');
        ldCall('playAudioID', el.dataset.ftAudioId || '');
    }
});

document.addEventListener('dblclick', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-ld-dblclick-action]')
        : null;
    if (!el) return;
    if (el.dataset.ftLdDblclickAction === 'open-new-tab') {
        event.preventDefault();
        ldCall('openLinkNewTab', el.dataset.ftUrl || '');
    }
});

document.addEventListener('change', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLInputElement>('[data-ft-ld-action="change-inventory-charges"]')
        : null;
    if (!el) return;
    ldCall('changeInventoryCharges', el.dataset.ftInventoryId || '', el.dataset.ftInputId || '');
});

// options/list_display/list_display_containers_inc.php (top-level isset + make_number_pretty)
export function renderContainersSubinclude(ctx: RenderCtx): string {
    const arr = ctx.arr;
    const rowKeys: Array<[string, string]> = [
        ['carrying_capacity_cu_ft', 'Cubic Feet'],
        ['carrying_capacity_cu_in', 'Cubic Inches'],
        ['carrying_capacity_people', 'People'],
        ['carrying_capacity_pounds', 'Pounds'],
    ];
    const gate = hasLayer(arr, 'carrying_capacity_cu_ft') || hasLayer(arr, 'carrying_capacity_cu_in')
        || hasLayer(arr, 'carrying_capacity_people') || hasTopLevel(arr, 'carrying_capacity_pounds')
        || hasLayer(arr, 'carrying_capacity_fixed_weight');
    if (!gate) return '';

    let html = '<div class="card-text ft-ld-block"><ul class="list-group border">'
        + '<li class="list-group-item list-group-item-primary fw-bold" aria-current="true">' + iconMap('container') + ' Carrying Capacity:</li>';
    for (const [key, label] of rowKeys) {
        if (!hasTopLevel(arr, key)) continue;
        html += '<li class="list-group-item">' + label + ': <span class="badge bg-dark border">' + escapeHtml(makeNumberPretty(topSlot0(arr, key))) + '</span></li>';
    }
    if (hasTopLevel(arr, 'carrying_capacity_fixed_weight')) {
        html += '<li class="list-group-item">Fixed Weight:  Weight of item is unchanged upon adding or removing contents from the container.  (Extradimentional storage.)</li>';
    }
    return html + '</ul></div>';
}

// options/list_display/list_display_shapeshifts_inc.php
// The "[...]" conditional branch builds a creature-select dropdown via build_item_type_item_arrs
// (DB query + form) — DEFERRED (out of read scope). Else branch (user-choice list) + the two
// inventory-remains flags are ported.
export function renderShapeshiftsSubinclude(ctx: RenderCtx): string {
    const arr = ctx.arr;
    const gate = hasTopLevel(arr, 'shapeshift') || hasTopLevel(arr, 'user_choice_1_shapeshift')
        || hasTopLevel(arr, 'user_choice_2_shapeshift') || hasTopLevel(arr, 'user_choice_3_shapeshift')
        || hasTopLevel(arr, 'user_choice_4_shapeshift');
    if (!gate) return '';

    let html = '';
    const shapeshiftRaw = String(topSlot0(arr, 'shapeshift'));
    const isConditional = hasTopLevel(arr, 'shapeshift') && shapeshiftRaw.indexOf('[') !== -1;
    if (isConditional) {
        // creature-select dropdown form — DEFERRED (PHP-rendered). Emit nothing.
    } else {
        html += renderUserChoiceListRead(ctx, 'shapeshift', { label: 'Shapeshift', headerClass: 'list-group-item-primary border fw-bold' });
    }
    // shapeshift_monster_template: a monster_templates-table id (NOT an items row). ld_ts_mount.php
    // resolves it against monster_templates into ctx.names as a light SHELL (id + name only — NOT folded
    // into .catalog / full-nested, to keep the ability blob lean), so nameOf() yields the template NAME.
    // Rendered like the Shapeshift block (header + name row). The name links to the template's own page
    // (openItemInfo is items-only — a monster template isn't an item — so we use monster_template.php).
    if (hasLayer(arr, 'shapeshift_monster_template')) {
        const mtId = String(topSlot0(arr, 'shapeshift_monster_template') ?? '');
        if (mtId) {
            const mtName = nameOf(ctx.names, mtId) || mtId;
            const mtUrl = BASE_URL + 'monster_template.php?id=' + encodeURIComponent(mtId);
            html += '<div class="card-text ft-ld-block"><ul class="list-group border">'
                + '<li class="list-group-item list-group-item-primary border fw-bold" aria-current="true">'
                + iconMap('shapeshift_monster_template') + ' Shapeshift Monster Template:</li>'
                + '<li class="list-group-item">' + iconMap('shapeshift_monster_template')
                + ' <a class="link-primary fw-bold" href="' + mtUrl + '" target="_blank" rel="noopener">'
                + escapeHtml(mtName) + '</a></li>'
                + '</ul></div>';
        }
    }
    html += renderSingleItem(ctx, 'shapeshift_inventory_remains_on_form', { contentType: 'label_only', label: 'Shapeshift Inventory Remains on Form:', liClass: 'list-group-item-success fw-bold', extraHtmlCallback: () => '<li class="list-group-item">When shapeshifting, the caster keeps all the inventory equipped into the shapeshifted form.</li>' });
    html += renderSingleItem(ctx, 'user_choice_1_shapeshift_inventory_remains_on_form', { contentType: 'label_only', label: 'User Choice Shapeshift Inventory Remains on Form:', liClass: 'list-group-item-success fw-bold', extraHtmlCallback: () => '<li class="list-group-item">When shapeshifting, the caster has a choice to keep all the inventory the caster has on the new form, or absorb it (default logic) into the shapeshifted form.</li>' });
    return html;
}

// PHP calc_average_height / calc_average_weight (functions.php)
function calcAverageHeight(baseHeight: unknown, modDice: unknown, modDie: unknown): string {
    const avgMod = Math.round((Number(modDice) || 0) * (Number(modDie) || 0) / 2);
    const avg = Math.trunc((Number(baseHeight) || 0) + avgMod);
    const feet = Math.floor(avg / 12);
    const inches = avg % 12;
    return feet + "' " + inches + "''";
}
function calcAverageWeight(heightDice: unknown, heightDie: unknown, weightBase: unknown, weightDice: unknown, weightDie: unknown): number {
    const avgHeightMod = Math.round((Number(heightDice) || 0) * (Number(heightDie) || 0) / 2);
    const totalHeight = 48 + avgHeightMod;
    const heightRatio = Math.pow(totalHeight / 48, 3);
    const avgWeightMod = Math.round((Number(weightDice) || 0) * (Number(weightDie) || 0) / 2);
    return Math.round((Number(weightBase) || 0) * heightRatio + avgWeightMod);
}

// options/list_display/list_display_ancestry_info_inc.php
export function renderAncestryInfoSubinclude(ctx: RenderCtx): string {
    const arr = ctx.arr;
    const T = String(itemsCol<string>(arr, 'item_type') ?? '');
    let html = '';

    // Branch 1: ancestry general info — gate isset(age_young) (top-level) && type != size_type
    if (hasTopLevel(arr, 'age_young') && T !== 'size_type') {
        html += '<div class="card-text ft-ld-block"><ul class="list-group border">'
            + '<li class="list-group-item list-group-item-info fw-bold" aria-current="true">' + iconMap('ancestry') + ' General Info:</li>';
        if (hasLayer(arr, 'creature_type')) {
            const ctId = String(layerOf(arr, 'creature_type') ?? '');
            html += '<li class="list-group-item"><span class="fw-bold">' + iconMap('creature_type') + ' Creature Type:</span> <span class="link-primary" role="button" ' + ldInfoAttrs(ctId) + '>' + iconMap(ctId) + ' ' + referenceNameHtml(ctx.names, ctId) + '</span></li>';
        }
        if (hasLayer(arr, 'creature_sub_type')) {
            html += '<li class="list-group-item"><span class="fw-bold">' + iconMap('creature_sub_type') + ' Creature Sub Types:</span><div class="d-flex flex-wrap gap-2 align-items-center mt-1">';
            for (const v of layerEntriesOf(arr, 'creature_sub_type')) {
                const id = scalarLayerSlot(v);
                html += '<span class="link-primary" role="button" ' + ldInfoAttrs(id) + '>' + referenceNameHtml(ctx.names, id) + '</span>';
            }
            html += '</div></li>';
        }
        const sizeId = String(layerOf(arr, 'size') ?? '');
        html += '<li class="list-group-item"><span class="fw-bold">Age Range (Young - Old): </span>' + escapeHtml(slotString(layerOf(arr, 'age_young') ?? '')) + ' to ' + escapeHtml(slotString(layerOf(arr, 'age_old') ?? '')) + ' </li>'
            + '<li class="list-group-item"><span class="fw-bold">Size:</span> <span role="button" ' + ldInfoAttrs(sizeId) + '>' + iconMap(sizeId) + '</span></li>'
            + '<li class="list-group-item"><span class="fw-bold">Average Height (ft): </span>' + escapeHtml(calcAverageHeight(layerOf(arr, 'base_height_in'), layerOf(arr, 'base_height_in_mod_dice'), layerOf(arr, 'base_height_in_mod_die'))) + '</li>'
            + '<li class="list-group-item"><span class="fw-bold">Average Weight (lbs): </span>' + escapeHtml(String(calcAverageWeight(layerOf(arr, 'base_height_in_mod_dice'), layerOf(arr, 'base_height_in_mod_die'), layerOf(arr, 'base_weight_lbs'), layerOf(arr, 'base_weight_lbs_mod_dice'), layerOf(arr, 'base_weight_lbs_mod_die'))) + ' lbs.') + '</li>';
        html += '</ul></div>';
    }

    // Branch 2: size_type general info
    if (T === 'size_type') {
        const bh = layerOf(arr, 'base_height_in'), bhd = layerOf(arr, 'base_height_in_mod_dice'), bhdie = layerOf(arr, 'base_height_in_mod_die');
        const bw = layerOf(arr, 'base_weight_lbs'), bwd = layerOf(arr, 'base_weight_lbs_mod_dice'), bwdie = layerOf(arr, 'base_weight_lbs_mod_die');
        html += '<div class="card-text ft-ld-block"><ul class="list-group border">'
            + '<li class="list-group-item list-group-item-info fw-bold" aria-current="true">' + iconMap('info') + ' General Info:</li>'
            + '<li class="list-group-item"><span class="fw-bold">Average Height (ft): </span>' + escapeHtml(calcAverageHeight(bh, bhd, bhdie)) + '</li>'
            + '<li class="list-group-item"><span class="fw-bold">Average Weight (lbs): </span>' + escapeHtml(makeNumberPretty(calcAverageWeight(bhd, bhdie, bw, bwd, bwdie))) + ' lbs.</li>'
            + '</ul></div>';
    }

    return html;
}

// options/list_display/list_display_spell_info_inc.php
export function renderSpellInfoSubinclude(ctx: RenderCtx): string {
    const arr = ctx.arr;
    if (!hasLayer(arr, 'spell_circle')) return '';
    const T = String(itemsCol<string>(arr, 'item_type') ?? '');
    let html = '<div class="card-text ft-ld-block"><ul class="list-group border">'
        + '<li class="list-group-item list-group-item-info fw-bold" aria-current="true">' + iconMap('spell') + ' Spell Info:</li>';

    if ((T === 'full_ability' || T === 'ability' || T === 'quick_ability' || T === 'free_ability') && hasLayer(arr, 'spell_level')) {
        html += '<li class="list-group-item"><span class="fw-bold">Spell Level:</span> ' + displaySpellLevelBadge(arr) + '</li>';
    }

    const spheres = layerEntriesOf(arr, 'spell_sphere');
    if (spheres.length > 0) {
        html += '<li class="list-group-item"><span class="fw-bold">Spell Sphere' + (spheres.length > 1 ? 's' : '') + ':</span> '
            + spheres.map(s => iconMap(scalarLayerSlot(s))).join(' ') + '</li>';
    }

    const circle = layerOf<string>(arr, 'spell_circle') ?? '';
    html += '<li class="list-group-item"><span class="fw-bold">Spell Circle:</span> ' + iconMap(circle) + ' ' + referenceNameHtml(ctx.names, circle) + '</li>';

    let comps = '';
    for (const c of ['spell_component_verbal', 'spell_component_somatic', 'spell_material_item_id', 'spell_component_material_consumed']) {
        if (hasLayer(arr, c)) comps += iconMap(c);
    }
    html += '<li class="list-group-item"><span class="fw-bold">Components:</span>&nbsp; ' + comps + '</li>';

    // Plain-text (no-badge) cast-time for this list row — keeps its own markup, but the label text
    // is sourced from the shared single-source map so it can't drift from the side-info card.
    let castTime = '';
    if (hasLayer(arr, 'spell_ritual')) castTime += iconMap('spell_ritual');
    const castTimeCode = String(layerOf<string>(arr, 'spell_casting_time') ?? '');
    if (isApCastTime(castTimeCode)) {
        castTime += iconMap(castTimeCode);
    } else if (SPELL_CASTING_TIME_LABELS[castTimeCode]) {
        castTime += iconMap('3ap') + ' ' + SPELL_CASTING_TIME_LABELS[castTimeCode];
    }
    html += '<li class="list-group-item"><span class="fw-bold">Casting Time:</span>&nbsp; ' + castTime + '</li>';

    return html + '</ul></div>';
}

// options/list_display/list_display_range_aoe_inc.php
export function renderRangeAoeSubinclude(ctx: RenderCtx): string {
    const arr = ctx.arr;
    if (!(hasLayer(arr, 'range') || hasLayer(arr, 'aoe_type') || hasLayer(arr, 'duration_int') || hasLayer(arr, 'max_targets'))) return '';
    let html = '<div class="card-text ft-ld-block"><ul class="list-group border">'
        + '<li class="list-group-item list-group-item-info fw-bold" aria-current="true">' + iconMap('parameters') + ' Parameters:</li>';

    if (hasLayer(arr, 'range')) {
        const rid = layerOf<string>(arr, 'range') ?? '';
        html += '<li class="list-group-item"><span class="fw-bold">Range:</span> <span role="button" ' + ldInfoAttrs(rid) + '>' + iconMap(rid) + '</span></li>';
    }

    if (hasLayer(arr, 'aoe_type')) {
        html += '<li class="list-group-item"><span class="fw-bold">';
        if (hasLayer(arr, 'persistent_aoe')) {
            html += '<span role="button" ' + ldInfoAttrs('ABDD3E12-7B39-484C-5E71-3AFC547B8201') + '>' + iconMap('persistent_aoe') + '</span>';
        }
        html += ' AOE:</span> <span class="badge bg-dark border">' + escapeHtml(slotString(layerOf(arr, 'aoe_distance') ?? '')) + '</span>';
        const depth = layerOf(arr, 'aoe_distance_depth');
        if (depth !== undefined && depth !== null) html += '<span class="badge bg-dark border ms-1">' + iconMap('aoe_distance_depth', 15, 15) + ' ' + escapeHtml(slotString(depth)) + '</span>';
        const height = layerOf(arr, 'aoe_distance_height');
        if (height !== undefined && height !== null) html += '<span class="badge bg-dark border ms-1">' + iconMap('aoe_distance_height', 15, 15) + ' ' + escapeHtml(slotString(height)) + '</span>';
        const aoeType = layerOf<string>(arr, 'aoe_type') ?? '';
        html += '<span class="ms-1" role="button" ' + ldInfoAttrs(aoeType) + '>' + iconMap(aoeType) + '</span>';
        if (hasLayer(arr, 'chooseable_targets_in_aoe')) html += ' | Selectable Targets in AOE';
        if (hasLayer(arr, 'aoe_follows_target')) html += ' | ' + iconMap('aoe_follows_target') + ' AOE Follows Target';
        if (hasLayer(arr, 'arcing_trajectory')) html += ' | ' + iconMap('arcing_trajectory') + ' Arcing Trajectory (indirect fire: lobs over obstacles, no sight of the landing point needed)';
        html += '</li>';
    }

    if (hasLayer(arr, 'max_targets')) {
        html += '<li class="list-group-item"><span class="fw-bold">Max Targets:</span> <span class="badge bg-dark border">' + escapeHtml(slotString(layerOf(arr, 'max_targets') ?? '')) + '</span></li>';
    }

    if (hasLayer(arr, 'duration_int') || hasLayer(arr, 'duration_type')) {
        html += '<li class="list-group-item"><span class="fw-bold">Duration:</span> ';
        if (hasLayer(arr, 'spell_concentration')) html += '<span role="button" ' + ldInfoAttrs('E7FDB796-26DA-E0F5-8F88-C21FE01D923B') + '>' + iconMap('concentration') + '</span> ';
        html += displayDuration(arr, false);
        html += '</li>';
    }

    if (hasLayer(arr, 'status_effect_applied_after_cast')) {
        html += '<li class="list-group-item"><span class="fw-bold mr-1 mb-1">Status Effect Applied After Cast:</span><ul class="list-group border">';
        for (const v of layerEntriesOf(arr, 'status_effect_applied_after_cast')) {
            const id = scalarLayerSlot(v);
            html += '<li class="list-group-item">' + iconMap(id) + ' <a class="link-primary" href="' + BASE_URL + 'item.php?id=' + escapeHtml(id) + '"> ' + referenceNameHtml(ctx.names, id) + '</a></li>';
        }
        html += '</ul></li>';
    }

    return html + '</ul></div>';
}

// SPELL RANGED ATTACK RANGE INCREASED (FAR BENDER family) — STANDALONE layer block (NOT a Parameter).
// Caster passive that raises a qualifying ranged-attack spell's band from FROM (floor) to TO; both
// layer values are range_type UUIDs. Leading icon = the layer's OWN (_to) icon; bg-info header.
export function renderSpellRangedAttackRangeIncreasedSubinclude(ctx: RenderCtx): string {
    const arr = ctx.arr;
    if (!hasLayer(arr, 'spell_ranged_attack_roll_range_increased_to')) return '';
    const fromId = layerOf<string>(arr, 'spell_ranged_attack_roll_range_increased_from');
    const toId = layerOf<string>(arr, 'spell_ranged_attack_roll_range_increased_to') ?? '';
    let html = '<div class="card-text ft-ld-block"><ul class="list-group border">'
        + '<li class="list-group-item">'
        + iconMap('spell_ranged_attack_roll_range_increased_to')
        + ' <span class="fw-bold">Spell Ranged Attack Range Increased:</span> ';
    if (fromId !== undefined && fromId !== null && fromId !== '') {
        html += '<span role="button" ' + ldInfoAttrs(fromId) + '>' + iconMap(fromId) + '</span> &rarr; ';
    }
    html += '<span role="button" ' + ldInfoAttrs(toId) + '>' + iconMap(toId) + '</span>';
    return html + '</li></ul></div>';
}

function numericSlotEq0(v: unknown): boolean {
    const n = Number(slotString(v));
    return !isNaN(n) && n === 0;
}

// options/list_display/list_display_spells_prepared_inc.php (read path — no useAbility buttons)
export function renderSpellsPreparedSubinclude(ctx: RenderCtx): string {
    const arr = ctx.arr;
    const lists = ['spell_prepared_1', 'spell_prepared_2', 'spell_prepared_3'];
    // bucket-first: get_item_arr.php returns pure-bucket, so spell_prepared_N lives under item_layer_types.
    const slotOf = (l: string) => arr?.item_layer_types?.[l] ?? arr?.[l];
    const present = lists.filter(l => { const s = slotOf(l); return s !== undefined && s !== null && (!Array.isArray(s) || s.length > 0); });
    if (present.length === 0) return '';

    const essence = layerOf<string>(arr, 'spellcasting_essence');
    const hasEncounterCreature = !!arr?.encounter_creatures?.encounter_creature_id;

    let html = '<div class="card-text ft-ld-block"><ul class="list-group border">'
        + '<li class="list-group-item list-group-item-info fw-bold" aria-current="true">'
        + (essence !== undefined && essence !== null ? iconMap(essence) + ' ' : '') + 'Spellcasting</li>';

    lists.forEach((listName, listIndex) => {
        const slot = slotOf(listName);
        if (slot === undefined || slot === null || (Array.isArray(slot) && slot.length === 0)) return;
        const entries = Array.isArray(slot) ? slot : [slot];

        const byLevel: Record<string, Array<{ id: string; name: string }>> = {};
        for (let lv = 0; lv <= 10; lv++) byLevel[String(lv)] = [];
        for (const entry of entries) {
            const spellId = scalarLayerSlot(entry);
            if (!spellId) continue;
            // Resolve a BARE-UUID-STRING / shell entry to its full def before reading spell_level +
            // name. A bare string carries neither inline, so it bucketed under '' and was DROPPED
            // (the render loop walks only levels 0..10). resolveNest falls back to the global finder,
            // so the def resolves even off an encounter-re-seeded blob (no own .catalog). Sibling of
            // the bare-string attack-ability bug. [[feedback_ftlayervalue_no_computed_bridge]]
            const def = (entry && typeof entry === 'object') ? entry : resolveNest(entry, arr);
            const level = (def && typeof def === 'object') ? String(layerOf(def, 'spell_level') ?? '') : '';
            // prefer the entry's own name (full-nest shells carry it); else the referenced-names map.
            const name = (def && typeof def === 'object' ? String(itemsCol<string>(def, 'item_name') ?? '') : '') || nameOf(ctx.names, spellId);
            if (byLevel[level] !== undefined) byLevel[level].push({ id: spellId, name });
        }

        const listLabel = (lists.length > 1 && listIndex > 0) ? ' (List ' + (listIndex + 1) + ')' : '';
        html += '<li class="list-group-item list-group-item-secondary fw-bold" aria-current="true">' + iconMap('spell_prepared_1') + ' Spells Prepared' + listLabel + ':</li>';
        for (let lv = 0; lv <= 10; lv++) {
            const ids = byLevel[String(lv)];
            if (!ids || ids.length === 0) continue;
            html += '<li class="list-group-item">' + iconMap('spell_level_' + lv) + ' | ';
            for (const { id: spellId, name } of ids) {
                html += '<span class="badge bg-light text-dark">'
                    + (hasEncounterCreature ? '<span role="button" ' + ldAttrs('use-ability', { itemId: spellId }) + ' class="mr-1">' + iconMap('cast_spell') + '</span>' : '')
                    + '<span role="button" ' + ldInfoAttrs(spellId) + ' data-ft-ld-dblclick-action="open-new-tab" data-ft-url="' + escapeHtml(BASE_URL + 'item.php?id=' + spellId) + '">' + referenceNameHtml(ctx.names, spellId, name) + '</span></span> ';
            }
            html += '</li>';
        }
    });

    return html + '</ul></div>';
}

// options/list_display/roll_types/list_display_roll_types_inc.php
// NB: PHP splices item_layer_types → top-level first, so all reads are bucket-aware.
// Has INTERACTIVE roll buttons (rollDice / useAbilityVariable1). For the dice arg the PHP
// used a bare $dice var; we use the (correct) layer value so the button works under full-replace.
export function renderRollTypesSubinclude(ctx: RenderCtx): string {
    const arr = ctx.arr;
    const AD80 = 'AD80D31D-898B-66CE-0894-1335D11B1529';        // Contesting Roll
    const WEAPON_BENDING = 'F964C0DA-54EA-1EB5-ED00-8E9AD50FFA53';
    const DC_ROLL = 'ED5B44EA-7438-9582-A388-04EFF342E460';
    // Crafting, gathering, lockpicking and excavation display a skill against a DC.
    const SKILL_VS_DC_ROLL_IDS = ['54DC30E0-923C-4BBF-A8CC-570B0068E8A2', 'FA17A3A2-3068-C84C-1EA5-C511F71FBD8B', 'CC2CEB6B-CEFB-481F-911A-A47A427D5EB2', 'E1C8D281-0DC7-4883-8381-5630E23AD99D'];
    const DAMAGE_ONLY = ['82BBB3B6-4646-48E8-A5A2-8DC0947A91D0', AD80, '77A044B7-7041-CA96-3C63-BB718B15A364', '2FE8DFB7-FE61-4862-AF71-3523262350A0', '3F85CA97-F9B2-A83A-FDE8-E523FD99346C', '5740CA49-006E-A7A0-B46B-6DDED6CD546C', 'DD280A3B-6F1D-4CE9-9EFD-505A74D0C07C'];

    if (!(hasLayer(arr, 'roll_type') || hasLayer(arr, 'skill_roll_contester') || hasLayer(arr, 'skill_roll_contestee') || hasLayer(arr, 'status_applied_on_hit') || hasLayer(arr, 'spell_upcastable'))) return '';

    const itemId = String(itemsCol<string>(arr, 'item_id') ?? '');
    const joinIcons = (layer: string) => layerEntriesOf(arr, layer).map(v => iconMap(scalarLayerSlot(v))).join(' | ');
    const damageTypeText = () => {
        let out = '', c = 0;
        for (const d of layerEntriesOf(arr, 'damage_type')) { if (c > 0) out += '|'; out += ' ' + iconMap(scalarLayerSlot(d)); c++; }
        return out;
    };

    let html = '<div class="card-text ft-ld-block"><ul class="list-group border">';

    // header li — roll type label
    html += '<li class="list-group-item list-group-item-danger fw-bold" aria-current="true">';
    if (hasLayer(arr, 'roll_type')) {
        const rt = String(layerOf(arr, 'roll_type') ?? '');
        html += '<span role="button" ' + ldInfoAttrs(rt) + '>' + iconMap(rt) + ' ' + referenceNameHtml(ctx.names, rt) + ':</span>';
    } else if (hasLayer(arr, 'next_spell_target_rider') && hasLayer(arr, 'skill_roll_contestee')) {
        html += iconMap(AD80) + ' Next Spell Rider &mdash; Contesting Roll:';
    } else {
        // Some non-roll abilities enter this block only because they have scaling/upcast fields.
        // "N/A" looked like missing data even though useful rows followed it.
        html += iconMap('roll_type') + ' Roll &amp; Scaling:';
    }
    html += '</li>';

    // Attack Type
    if (hasLayer(arr, 'weapon_type')) {
        html += '<li class="list-group-item"><span class="fw-bold mr-1">Attack Type:</span> ' + referenceNameHtml(ctx.names, String(layerOf(arr, 'weapon_type') ?? '')) + '</li>';
    }

    // Contesting skill roll
    if (hasLayer(arr, 'contesting_roll') || hasLayer(arr, 'skill_roll_contestee')) {
        html += '<li class="list-group-item">';
        if (hasLayer(arr, 'ability_dc')) html += '<span class="badge bg-warning text-dark">' + escapeHtml(slotString(layerOf(arr, 'ability_dc') ?? '')) + '</span>';
        if (hasLayer(arr, 'contesting_roll_dc')) html += '<span class="badge bg-warning text-dark">' + escapeHtml(slotString(layerOf(arr, 'contesting_roll_dc') ?? '')) + '</span>';
        if (hasLayer(arr, 'contesting_roll')) html += iconMap(String(layerOf(arr, 'contesting_roll') ?? ''));
        if (hasLayer(arr, 'skill_roll_contester')) {
            html += joinIcons('skill_roll_contester') + ' vs. ';
        } else if (!hasLayer(arr, 'skill_roll_contester') && hasLayer(arr, 'ability_dc')) {
            html += ' vs. ';
        } else {
            html += iconMap(WEAPON_BENDING) + ' vs. ';
        }
        if (hasLayer(arr, 'skill_roll_contestee')) html += joinIcons('skill_roll_contestee');
        if (hasLayer(arr, 'contesting_roll_successful_for_half_damage')) html += '<span class="badge bg-warning text-dark border">&#189;</span> ';
        if (hasLayer(arr, 'contesting_roll_skipped_for_same_team_type')) html += ' <span class="badge bg-success border">' + iconMap('contesting_roll_skipped_for_same_team_type') + ' Same team type do not contest</span>';
        html += '</li>';
    }

    // DC ROLL
    if (hasLayer(arr, 'skill_roll_contester') && hasLayer(arr, 'ability_dc') && hasLayer(arr, 'roll_type') && String(layerOf(arr, 'roll_type')) === DC_ROLL) {
        html += '<li class="list-group-item">' + joinIcons('skill_roll_contester') + ' vs. <span class="badge bg-warning text-dark">' + escapeHtml(slotString(layerOf(arr, 'ability_dc') ?? '')) + '</span></li>';
    }

    // CRAFTING / GATHERING / LOCKPICKING ROLL
    if (hasLayer(arr, 'skill_roll_contester') && hasLayer(arr, 'ability_dc') && hasLayer(arr, 'roll_type') && SKILL_VS_DC_ROLL_IDS.indexOf(String(layerOf(arr, 'roll_type'))) !== -1) {
        html += '<li class="list-group-item">' + joinIcons('skill_roll_contester') + ' vs. <span class="badge bg-warning text-dark">' + escapeHtml(slotString(layerOf(arr, 'ability_dc') ?? '')) + '</span></li>';
    }

    // DAMAGE ROLL
    if (hasLayer(arr, 'roll_type') && hasLayer(arr, 'die')) {
        html += '<li class="list-group-item">';
        const rt = String(layerOf(arr, 'roll_type') ?? '');
        const dmgType0 = String(layerOf(arr, 'damage_type') ?? '');
        const actionAttrs = (DAMAGE_ONLY.indexOf(rt) !== -1)
            ? ldAttrs('use-ability-variable1', { itemId, variable1: 'damage_only' })
            : ldAttrs('roll-dice', {
                rollType: rt || '82BBB3B6-4646-48E8-A5A2-8DC0947A91D0',
                damageType: dmgType0,
                itemId,
                dice: slotString(layerOf(arr, 'dice') ?? ''),
                die: slotString(layerOf(arr, 'die') ?? ''),
                mod: slotString(layerOf(arr, 'mod') ?? '0') || '0',
            });
        let dmgText = slotString(layerOf(arr, 'dice') ?? '') + 'd' + slotString(layerOf(arr, 'die') ?? '');
        if (hasLayer(arr, 'mod')) dmgText += ' + ' + slotString(layerOf(arr, 'mod') ?? '');
        if (hasLayer(arr, 'damage_type')) dmgText += damageTypeText();
        html += '<button type="button" class="btn btn-sm btn-danger" ' + actionAttrs + '>' + dmgText + '</button>';

        // versatile button
        if (hasLayer(arr, 'versatile_dice')) {
            let vText = slotString(layerOf(arr, 'versatile_dice') ?? '') + 'd' + slotString(layerOf(arr, 'versatile_die') ?? '');
            if (hasLayer(arr, 'versatile_dice_damage_mod')) vText += ' + ' + slotString(layerOf(arr, 'versatile_dice_damage_mod') ?? '');
            if (hasLayer(arr, 'mod')) vText += ' + ' + slotString(layerOf(arr, 'mod') ?? '');
            vText += ' ' + iconMap(String(layerOf(arr, 'damage_type') ?? ''));
            html += '<button type="button" class="btn btn-sm btn-danger position-relative ms-2" ' + ldAttrs('use-ability-variable1', { itemId, variable1: 'damage_only' }) + '>' + vText
                + '<span class="ft-ld-versatile-badge position-absolute top-0 start-50 translate-middle badge text-white rounded-pill bg-dark">2 Handed</span></button>';
        }
        html += '</li>';

        if (hasLayer(arr, 'damage_heals_caster')) html += '<li class="list-group-item">' + iconMap('damage_heals_caster') + ' Damage heals the ' + iconMap('caster') + '</li>';
        if (hasLayer(arr, 'healing_damages_caster')) html += '<li class="list-group-item">' + iconMap('healing_damages_caster') + ' Healing damages the ' + iconMap('caster') + '</li>';
        if (hasLayer(arr, 'effect_ignores_damage_type_resistance')) html += '<li class="list-group-item">' + iconMap('effect_ignores_damage_type_resistance') + ' The damage roll effect from this spell or ability ignores any ' + iconMap('target') + ' ' + iconMap('damage_type_resistance') + ' damage type resistance.</li>';
        if (hasLayer(arr, 'effect_ignores_damage_type_immunity')) html += '<li class="list-group-item">' + iconMap('effect_ignores_damage_type_immunity') + ' The damage roll effect from this spell or ability ignores any ' + iconMap('target') + ' ' + iconMap('damage_type_immunity') + ' damage type immunities.</li>';
        if (hasLayer(arr, 'no_regeneration_if_damage_type_taken')) {
            html += '<li class="list-group-item">No regeneration if damage type taken over turn: ';
            for (const v of layerEntriesOf(arr, 'no_regeneration_if_damage_type_taken')) {
                const id = scalarLayerSlot(v);
                html += '<span role="button" ' + ldInfoAttrs(id) + '>' + iconMap(id) + '</span>';
            }
            html += '</li>';
        }
    }

    // UPCASTABLE / scalable cantrip
    if (hasLayer(arr, 'spell_upcastable') || hasLayer(arr, 'spell_scalable_cantrip')) {
        html += '<li class="list-group-item"><span class="fw-bold mr-1">';
        if (hasLayer(arr, 'spell_upcastable')) html += 'Spell Upcastable';
        if (hasLayer(arr, 'spell_scalable_cantrip')) html += 'Scalable Cantrip';
        html += ':</span>';

        if (hasLayer(arr, 'die_upcast')) {
            let uText = slotString(layerOf(arr, 'dice_upcast') ?? '') + 'd' + slotString(layerOf(arr, 'die_upcast') ?? '');
            if (hasLayer(arr, 'mod_upcast')) uText += ' + ' + slotString(layerOf(arr, 'mod_upcast') ?? '');
            if (hasLayer(arr, 'damage_type')) uText += damageTypeText();
            html += '<button type="button" class="btn btn-sm btn-danger" ' + ldAttrs('roll-dice', {
                rollType: '82BBB3B6-4646-48E8-A5A2-8DC0947A91D0',
                damageType: String(layerOf(arr, 'damage_type') ?? ''),
                itemId,
                dice: slotString(layerOf(arr, 'dice_upcast') ?? ''),
                die: slotString(layerOf(arr, 'die_upcast') ?? ''),
                mod: slotString(layerOf(arr, 'mod_upcast') ?? '0') || '0',
            }) + '>' + uText + '</button>';
        }
        if (layerOf(arr, 'mod_upcast') === undefined && layerOf(arr, 'die_upcast') === undefined) html += 'Yes ';
        if (hasLayer(arr, 'spell_upcast_additional_targets_int')) html += " | <span class='badge bg-dark border border'>" + escapeHtml(slotString(layerOf(arr, 'spell_upcast_additional_targets_int') ?? '')) + '</span> Additional Target(s) per Mana Upcasted';
        if (hasLayer(arr, 'spell_upcast_additional_projectiles_created')) html += iconMap('spell_upcast_additional_projectiles_created') + " | <span class='badge bg-dark border border-'>" + escapeHtml(slotString(layerOf(arr, 'spell_upcast_additional_projectiles_created') ?? '')) + '</span> Additional Projectile(s) Created per Mana Upcasted';
        if (hasLayer(arr, 'spell_upcast_increase_aoe_ft')) html += iconMap('spell_upcast_increase_aoe_ft') + " | <span class='badge bg-dark border border'>" + escapeHtml(slotString(layerOf(arr, 'spell_upcast_increase_aoe_ft') ?? '')) + '</span> Additional AOE Distance (ft) per Mana Upcasted';
        if (hasLayer(arr, 'spell_upcast_duration_type') && hasLayer(arr, 'spell_upcast_duration_int')) html += iconMap('spell_upcast_duration_type') + " | <span class='badge bg-dark border'>" + escapeHtml(slotString(layerOf(arr, 'spell_upcast_duration_int') ?? '')) + "</span> <span class='badge bg-dark border border'>" + escapeHtml(slotString(layerOf(arr, 'spell_upcast_duration_type') ?? '')) + '</span> Additional Duration per Mana Upcasted';
        html += '</li>';
    }

    // immune_to_ability_upon_successful_contesting_roll_till_long_rest
    if (hasLayer(arr, 'immune_to_ability_upon_successful_contesting_roll_till_long_rest')) {
        html += '<li class="list-group-item"><span class="fw-bold mr-1 mb-1">' + iconMap('immune_to_ability_upon_successful_contesting_roll_till_long_rest') + ' Immune to ability on SUCCESSFUL Contesting Roll until long rest:</span> Yes</li>';
    }

    // spell_scalable_cantrip detail
    if (hasLayer(arr, 'spell_scalable_cantrip')) {
        html += '<li class="list-group-item"><span class="fw-bold mr-1 mb-1">' + iconMap('scalable_cantrip') + ' Scalable cantrip:</span> Damage dice automatically increases by <span class=\'badge bg-warning border\'>1</span>, each time, as the character\'s TOTAL CASTER LEVEL reaches: <span class=\'badge bg-dark border\'>9</span>, <span class=\'badge bg-dark border\'>19</span>, and <span class=\'badge bg-dark border\'>33</span></span></li>';
    }

    return html + '</ul></div>';
}

// options/list_display/list_display_status_effect_parameters_inc.php
// NB: PHP splices item_layer_types → top-level first, so all isset/array_key_exists checks
// see bucketed layers — i.e. bucket-aware (hasLayer / layerEntriesOf) is the correct mirror.
export function renderStatusEffectParametersSubinclude(ctx: RenderCtx): string {
    const arr = ctx.arr;
    const AD80 = 'AD80D31D-898B-66CE-0894-1335D11B1529';
    const FAILED = ' <span class="badge bg-danger">FAILED</span>';
    const RESISTED = ' <span class="badge bg-success">RESISTED</span>';

    // status-list blocks: [layer, headerIconKey, headerHtml, perEntryIconKey?]
    const statusBlocks: Array<[string, string, string, string?]> = [
        ['status_effect_applied_to_target_after_contesting_roll_failed', 'status_effect', 'Applied to ' + iconMap('target') + ' after ' + iconMap(AD80) + FAILED + ':'],
        ['status_effect_applied_to_target_on_hit', 'status_effect', 'Applied to ' + iconMap('target') + ' on HIT:'],
        ['status_effect_applied_to_target_after_cast', 'status_effect', 'Applied to ' + iconMap('target') + ' after Cast:'],
        ['status_effect_removed_from_target_after_cast', 'status_effect', 'Removed from ' + iconMap('target') + ' after Cast:'],
        ['status_effect_removed_from_target_after_contesting_roll_failed', 'status_effect', 'Removed from ' + iconMap('target') + ' after ' + iconMap(AD80) + FAILED + ':'],
        ['status_effect_removed_from_target_on_hit', 'status_effect', 'Removed from ' + iconMap('target') + ' on HIT:'],
        ['diseased_applied_to_target_after_contesting_roll_failed', 'disease', 'Disease Applied to ' + iconMap('target') + ' after ' + iconMap(AD80) + FAILED + ':', 'disease'],
        ['user_choice_1_diseased_applied_to_target_after_contesting_roll_failed', 'disease', 'User Choice Disease Applied to ' + iconMap('target') + ' after ' + iconMap(AD80) + FAILED + ':', 'disease'],
        ['status_effect_applied_to_caster_after_contesting_roll_failed', 'status_effect', 'Applied to ' + iconMap('caster') + ' after ' + iconMap(AD80) + FAILED + ':'],
        // The COMPLEMENT branch: the CONTESTEE won, so the caster LOST the contest. Every other row
        // in this table fires when the caster WINS — this is the only one that fires when they don't.
        ['status_effect_applied_to_caster_when_contestee_wins', 'status_effect', 'Applied to ' + iconMap('caster') + ' when the ' + iconMap('target') + ' WINS the ' + iconMap(AD80) + RESISTED + ':'],
        ['status_effect_applied_to_caster_on_hit', 'status_effect', 'Applied to ' + iconMap('caster') + ' on HIT:'],
        ['status_effect_applied_to_caster_after_cast', 'status_effect', 'Applied to ' + iconMap('caster') + ' after Cast:'],
        // A PASSIVE held by the caster for the cast's duration (Psion Pyrokinetic Fortress, 2026-10-07): an enemy aura's self-buff.
        ['passive_ability_applied_to_caster_after_cast', 'passive_ability', 'Passive applied to ' + iconMap('caster') + ' for the cast\'s duration:'],
        ['status_effect_removed_from_caster_after_cast', 'status_effect', 'Removed from ' + iconMap('caster') + ' after Cast:'],
        ['status_effect_removed_from_caster_after_contesting_roll_failed', 'status_effect', 'Removed from ' + iconMap('caster') + ' after ' + iconMap(AD80) + FAILED + ':'],
        ['status_effect_removed_from_caster_on_hit', 'status_effect', 'Removed from ' + iconMap('caster') + ' on HIT:'],
        // R-173 (2026-10-07): the lingering effect ends early when its holder gains one of these.
        ['effect_ends_when_target_gains_status_effect', 'status_effect', 'Effect <span class="badge bg-secondary">ENDS</span> when its holder <span class="badge bg-danger">GAINS</span>:'],
    ];

    // timing-flag rows: [layer, innerHtml]
    const flags: Array<[string, string]> = [
        // Flag twin of the two disease-applier selects above — the disease is rolled, not named.
        ['random_disease_applied_to_target_after_contesting_roll_failed', iconMap('disease') + ' <span class="badge bg-danger">RANDOM</span> Disease Applied to ' + iconMap('target') + ' after ' + iconMap(AD80) + FAILED + ' (plus the Diseased condition)'],
        ['target_retries_contesting_roll_on_start_of_turn', iconMap('target') + ' RETRIES ' + iconMap(AD80) + ' on <span class="badge bg-primary">START</span> of ' + iconMap('target') + ' turn'],
        ['target_retries_contesting_roll_on_end_of_turn', iconMap('target') + ' RETRIES ' + iconMap(AD80) + ' on <span class="badge bg-warning">END</span> of ' + iconMap('target') + ' turn'],
        ['target_retries_contesting_roll_on_damage_taken', iconMap('target') + ' RETRIES ' + iconMap(AD80) + ' immediately whenever it <span class="badge bg-danger">TAKES DAMAGE</span> from the ' + iconMap('caster') + ' or the ' + iconMap('caster') + ' allies'],
        ['status_effect_applied_is_caster_choice', 'The ' + iconMap('caster') + ' <span class="badge bg-primary">CHOOSES ONE</span> of the listed status effects when casting; only that one is applied'],
        ['effect_starts_on_target_start_of_turn', 'Effect <span class="badge bg-primary">STARTS</span> on the <span class="badge bg-success">START</span> of the ' + iconMap('target') + ' turn'],
        ['effect_starts_on_target_end_of_turn', 'Effect <span class="badge bg-primary">STARTS</span> on the <span class="badge bg-warning">END</span> of the ' + iconMap('target') + ' turn'],
        ['effect_ends_on_target_start_of_turn', 'Effect <span class="badge bg-secondary">ENDS</span> on ' + iconMap('target') + ' <span class="badge bg-success">START</span> of turn'],
        ['effect_ends_on_target_end_of_turn', 'Effect <span class="badge bg-secondary">ENDS</span> on ' + iconMap('target') + ' <span class="badge bg-warning">END</span> of turn'],
        ['effect_ends_on_start_of_caster_next_turn', 'Effect <span class="badge bg-secondary">ENDS</span> on the <span class="badge bg-success">START</span> of the ' + iconMap('caster') + ' <span class="badge bg-info">NEXT</span> turn'],
        ['effect_ends_on_end_of_caster_next_turn', 'Effect <span class="badge bg-secondary">ENDS</span> on the <span class="badge bg-warning">END</span> of the ' + iconMap('caster') + ' <span class="badge bg-info">NEXT</span> turn'],
        // R-118 exit plan / R-172 (2026-10-07, Psion Metamorph Elastic Joints / Monstrous Transformation).
        ['effect_ends_on_combat_end', 'Effect <span class="badge bg-secondary">ENDS</span> when <span class="badge bg-danger">COMBAT ENDS</span>'],
        ['immune_to_ability_after_effect_until_combat_end', 'A ' + iconMap('target') + ' that gained this effect cannot benefit from it again until <span class="badge bg-danger">COMBAT ENDS</span>'],
        ['effect_ends_on_end_of_your_next_turn', 'Effect <span class="badge bg-secondary">ENDS</span> on the <span class="badge bg-warning">END</span> of your <span class="badge bg-info">NEXT</span> turn'],
        ['effect_occurs_on_move_action', 'Effect <span class="badge bg-primary">STARTS</span> on MOVE action'],
        ['effect_ends_on_current_end_turn', 'Effect <span class="badge bg-danger">ENDS</span> on CURRENT <span class="badge bg-warning">END</span> turn'],
    ];

    // gate: any status block or timing flag
    const gateLayers = [...statusBlocks.map(b => b[0]), ...flags.map(f => f[0])];
    if (!gateLayers.some(l => hasLayer(arr, l))) return '';

    let html = '<div class="card-text ft-ld-block"><ul class="list-group border">'
        + '<li class="list-group-item list-group-item-info fw-bold" aria-current="true">' + iconMap('status_effect_parameters') + ' Status Effect Parameters:</li>';

    for (const [layer, headerIcon, headerHtml, perEntryIcon] of statusBlocks) {
        if (!hasLayer(arr, layer)) continue;
        let rows = '';
        for (const v of layerEntriesOf(arr, layer)) {
            const id = scalarLayerSlot(v);
            const icon = perEntryIcon ? iconMap(perEntryIcon) : iconMap(id);
            rows += '<li class="list-group-item">' + icon + ' <a class="link-primary" href="' + BASE_URL + 'item.php?id=' + escapeHtml(id) + '" target="_blank"> ' + referenceNameHtml(ctx.names, id) + '</a></li>';
        }
        html += '<li class="list-group-item"><ul class="list-group border"><li class="list-group-item active fw-bold" aria-current="true">' + iconMap(headerIcon) + ' ' + headerHtml + '</li>' + rows + '</ul></li>';
    }

    for (const [layer, inner] of flags) {
        if (!hasLayer(arr, layer)) continue;
        html += '<li class="list-group-item"><span class="fw-bold mr-1 mb-1">' + iconMap(layer) + ' ' + inner + '</span></li>';
    }

    return html + '</ul></div>';
}

// All six saved essence families retain their creator subtotal classes and values.
interface EssenceFamily {
    prefix: string;       // layer-name prefix: '' | 'ancestral_' | 'background_' | 'feat_' | 'ability_' | 'inventory_'
    headerIcon: string;   // icon_ph key for the section header
    headerLabel: string;  // section header text
    selectClass: string;  // hidden-input class
    ucSuffix: string;     // user_choice_N_<ucSuffix> key suffix
    selectFullName: boolean; // hidden-input value uses the full prefixed layer name vs the bare essence name
}

// Regular/ancestral hidden values use prefixed names; the other subtotal families
// use bare essence names, as consumed by calcEssences.
const ESSENCE_FAMILIES: EssenceFamily[] = [
    { prefix: '',           headerIcon: 'essence',            headerLabel: 'Essences:',            selectClass: 'essence_select',            ucSuffix: 'essence',            selectFullName: true  },
    { prefix: 'ancestral_', headerIcon: 'ancestral_essences',  headerLabel: 'Ancestral Essences:',  selectClass: 'ancestral_essence_select',  ucSuffix: 'essence_ancestry',    selectFullName: true  },
    { prefix: 'background_', headerIcon: 'background_essences', headerLabel: 'Background Essences:', selectClass: 'background_essence_select', ucSuffix: 'essence_background', selectFullName: false },
    { prefix: 'feat_',      headerIcon: 'feat_essences',       headerLabel: 'Feat Essences:',       selectClass: 'feat_essence_select',       ucSuffix: 'essence_feat',       selectFullName: false },
    { prefix: 'ability_',   headerIcon: 'ability_essences',    headerLabel: 'Ability Essences:',    selectClass: 'ability_essence_select',    ucSuffix: 'essence_ability',    selectFullName: false },
    { prefix: 'inventory_', headerIcon: 'inventory_essences',  headerLabel: 'Inventory Essences:',  selectClass: 'inventory_essence_select',  ucSuffix: 'essence_inventory',  selectFullName: false },
];

function renderEssenceFamily(ctx: RenderCtx, fam: EssenceFamily, choice?: PropertyChoiceRenderer): string {
    const arr = ctx.arr;

    let open = Object.keys(ESSENCE_CATALOG).some(vn => hasLayer(arr, fam.prefix + vn));
    if (!open) {
        for (let i = 1; i <= 4; i++) {
            if (hasLayer(arr, 'user_choice_' + i + '_' + fam.ucSuffix)) { open = true; break; }
        }
    }
    if (!open) return '';

    let html = '<div class="card-text ft-ld-block"><ul class="list-group border">'
        + '<li class="list-group-item list-group-item-primary fw-bold" aria-current="true">' + iconMap(fam.headerIcon) + ' ' + fam.headerLabel + '</li>';
    for (const [vn, meta] of Object.entries(ESSENCE_CATALOG)) {
        const layerName = fam.prefix + vn;
        if (!hasLayer(arr, layerName)) continue;
        const entries = layerEntriesOf(arr, layerName);
        const token = layerScoringToken(entries[0] ?? '') ?? '';
        const selectName = fam.selectFullName ? layerName : vn;
        html += '<li class="list-group-item">' + iconMap(meta.item_id) + ' ' + escapeHtml(meta.item_name) + ': <span class="badge bg-dark border">' + escapeHtml(token) + '</span></li>'
            + '<input class="' + fam.selectClass + '" type="hidden" value="' + escapeHtml(selectName + '|' + token.replace(/\+/g, '')) + '">';
    }
    for (let i = 1; i <= 4; i++) {
        const key = 'user_choice_' + i + '_' + fam.ucSuffix;
        const slot = layerSlot(arr, key);
        if (slot === undefined || slot === null) continue;
        const entries = layerEntriesOf(arr, key);
        html += choice ? choice(key, entries, fam.selectClass) : renderUserChoiceReadRow(ctx, entries, fam.ucSuffix);
    }
    return html + '</ul></div>';
}

export function renderEssencesSubinclude(ctx: RenderCtx, choice?: PropertyChoiceRenderer): string {
    const T = String(itemsCol<string>(ctx.arr, 'item_type') ?? '');
    if (T === 'creature' || T === 'player_character') return '';
    return ESSENCE_FAMILIES.map(fam => renderEssenceFamily(ctx, fam, choice)).join('');
}

export function renderInnateSpellsSubinclude(ctx: RenderCtx, choice?: PropertyChoiceRenderer, context?: PropertyFormContext): string {
    const layer = 'innate_spell_per_long_rest';
    if (!choice) return renderUserChoiceListRead(ctx, layer, { labelSingularPlural: ['Innate Spell per Long Rest', 'Innate Spell(s) per Long Rest'], headerClass: 'list-group-item-primary fw-bold', sortItems: true });
    const entries = sortLayerEntriesByRequirements(layerEntriesOf(ctx.arr, layer), ctx.arr).filter(entry => {
        const required = layerOf(resolveNest(entry, ctx.arr), 'requires_class_level');
        return context?.class_level == null || required == null || Number(required) <= context.class_level;
    });
    const rows = renderCardListItemLayerRead(ctx, layer, entries)
        + [1, 2, 3, 4].map(i => {
            const key = `user_choice_${i}_${layer}`, entries = layerEntriesOf(ctx.arr, key);
            return entries.length ? choice(key, entries) : '';
        }).join('');
    return rows ? '<div class="card-text ft-ld-block"><ul class="list-group border"><li class="list-group-item list-group-item-primary fw-bold">'
        + iconMap(layer) + ' Innate Spell(s) per Long Rest:</li>' + rows + '</ul></div>' : '';
}

// options/list_display/list_display_ability_resets_inc.php (display-only; render_card_list_item_layer rows)
export function renderAbilityResetsSubinclude(ctx: RenderCtx): string {
    const arr = ctx.arr;
    let html = '';

    // "Charges & Resets" block — charges / resets / recharge. Self-describing header on the
    // ft-ability-hdr scheme (ft-ah--charged = yellow + bordered icon), not the vague green "Ability:".
    if (hasLayer(arr, 'ability_resets_short_rest') || hasLayer(arr, 'ability_resets_long_rest') || hasLayer(arr, 'ability_resets_each_combat') || hasLayer(arr, 'ability_charges') || hasLayer(arr, 'recharge_range_begin') || hasLayer(arr, 'recharge_range_end')) {
        html += '<div class="card-text ft-ld-block"><ul class="list-group border"><li class="list-group-item ft-ability-hdr ft-ah--charged fw-bold" aria-current="true">' + iconMap('ability_charges') + ' Charges &amp; Resets:</li>';
        if (hasLayer(arr, 'ability_charges')) {
            html += '<li class="list-group-item ft-ld-single-item" aria-current="true"><span class="fw-bold">' + iconMap('charges') + ' Charges:</span> <span class="badge bg-dark border border-light">' + escapeHtml(slotString(layerOf(arr, 'ability_charges') ?? '')) + '</span></li>';
        }
        if (hasLayer(arr, 'ability_resets_short_rest') || hasLayer(arr, 'ability_resets_long_rest') || hasLayer(arr, 'ability_resets_each_combat')) {
            html += '<li class="list-group-item ft-ld-single-item" aria-current="true"><span class="fw-bold mr-2">' + iconMap('ability_resets_long_rest') + ' Resets:</span>';
            if (hasLayer(arr, 'ability_resets_short_rest')) html += '<span class="badge bg-dark border border-light">Short Rest</span>';
            if (hasLayer(arr, 'ability_resets_long_rest')) html += '<span class="badge bg-dark border border-light">Long Rest</span>';
            if (hasLayer(arr, 'ability_resets_each_combat')) html += '<span class="badge bg-dark border border-light">Each Combat</span>';
            html += '</li>';
        }
        if (hasLayer(arr, 'recharge_range_begin') || hasLayer(arr, 'recharge_range_end')) {
            const begin = slotString(layerOf(arr, 'recharge_range_begin') ?? '');
            const end = slotString(layerOf(arr, 'recharge_range_end') ?? '');
            html += '<li class="list-group-item ft-ld-single-item" aria-current="true"><span class="fw-bold mr-2">' + iconMap('recharge_range_begin') + ' Recharge:</span>';
            html += (begin === end)
                ? '<span class="badge bg-dark border border-light">' + escapeHtml(begin) + '</span>'
                : '<span class="badge bg-dark border border-light">' + escapeHtml(begin) + '</span> - <span class="badge bg-dark border border-light">' + escapeHtml(end) + '</span>';
            html += '</li>';
        }
        html += '</ul></div>';
    }

    // qty helper for the charge-op headers (layer_value(qtyLayer) badge if non-null)
    const qtyBadge = (qtyLayer: string) => {
        const v = layerOf(arr, qtyLayer);
        return (v !== undefined && v !== null) ? "<span class='badge bg-dark border border-light'>" + escapeHtml(slotString(v)) + '</span>' : '';
    };
    const hasQty = (qtyLayer: string) => layerOf(arr, qtyLayer) !== undefined && layerOf(arr, qtyLayer) !== null;

    // repetitive charge-op blocks: top-level isset gate → header + card_list_item_layer rows
    const opBlock = (layer: string, headerColor: string, headerHtml: string) => {
        if (!hasLayer(arr, layer)) return '';
        return '<div class="card-text ft-ld-block"><ul class="list-group border"><li class="list-group-item ' + headerColor + ' fw-bold" aria-current="true">' + iconMap(layer) + ' ' + headerHtml + '</li>'
            + renderCardListItemLayerRead(ctx, layer, layerEntriesOf(arr, layer)) + '</ul></div>';
    };

    const consQ = (hasQty('consumes_charges_qty') ? ' ' + qtyBadge('consumes_charges_qty') : '');
    const consS = (hasQty('consumes_charges_qty') ? 's' : '');
    html += opBlock('consumes_ability_charges', 'list-group-item-danger', 'Consumes' + consQ + ' Ability Charge' + consS + ':');
    html += opBlock('consumes_ability_charges_from_caster_after_contesting_roll_failed', 'list-group-item-danger', 'Consumes' + consQ + ' Ability Charge' + consS + ' after ' + iconMap('AD80D31D-898B-66CE-0894-1335D11B1529') + ' Failed:');

    const addQ = (hasQty('add_charges_qty') ? ' ' + qtyBadge('add_charges_qty') : '');
    const addS = (hasQty('add_charges_qty') ? 's' : '');
    html += opBlock('add_charges_to_passive_ability', 'list-group-item-dark', 'Add' + addQ + ' Charge' + addS + ' to Passive Ability:');
    html += opBlock('add_charges_to_ability', 'list-group-item-dark', 'Add' + addQ + ' Charge' + addS + ' to Ability:');

    const rbq = hasQty('rechargeable_by_qty') ? slotString(layerOf(arr, 'rechargeable_by_qty')) : null;
    const rbBadge = rbq !== null ? "<span class='badge bg-dark border border-light'>" + escapeHtml(rbq) + '</span>' : '';
    const rbS = rbq !== null ? 's' : '';
    html += opBlock('rechargeable_by_passive_ability', 'list-group-item-dark', 'Rechargeable by ' + rbBadge + ' Charge' + rbS + ' from Passive Ability:');
    html += opBlock('rechargeable_by_ability', 'list-group-item-dark', 'Rechargeable by ' + rbBadge + ' Charge' + rbS + ' from Ability:');

    const mbQ = (hasQty('maintained_by_qty') ? ' ' + qtyBadge('maintained_by_qty') : '');
    const mbS = (hasQty('maintained_by_qty') ? 's' : '');
    html += opBlock('maintained_by_passive_ability', 'list-group-item-dark', 'Maintained by' + mbQ + ' Charge' + mbS + ' from Passive Ability, per round:');
    html += opBlock('maintained_by_ability', 'list-group-item-dark', 'Maintained by' + mbQ + ' Charge' + mbS + ' from Ability, per round:');

    const incS = hasLayer(arr, 'increase_max_charges_by_qty') ? 's by ' + qtyBadge('increase_max_charges_by_qty') : '';
    html += opBlock('increase_max_charges_of_passive_ability', 'list-group-item-dark', 'Increases Max Charge' + incS + ' of Passive Ability:');
    html += opBlock('increase_max_charges_of_ability', 'list-group-item-dark', 'Increases Max Charge' + incS + ' of Ability:');
    const decS = hasLayer(arr, 'decrease_max_charges_by_qty') ? 's by ' + qtyBadge('decrease_max_charges_by_qty') : '';
    html += opBlock('decrease_max_charges_of_passive_ability', 'list-group-item-dark', 'Decreases Max Charge' + decS + ' of Passive Ability:');
    html += opBlock('decrease_max_charges_of_ability', 'list-group-item-dark', 'Decreases Max Charge' + decS + ' of Ability:');
    html += opBlock('ability_charges_not_required', 'list-group-item-dark', 'No Ability Charges Can Be Consumed While Active:');

    // Keep target-facing charge fields in their own bordered group.
    if (hasLayer(arr, 'consume_ability_charges_for_target')
        || hasLayer(arr, 'add_charges_to_ability_for_target')
        || hasLayer(arr, 'maintained_by_ability_for_target')
        || hasLayer(arr, 'increase_max_charges_of_ability_for_target')
        || hasLayer(arr, 'decrease_max_charges_of_ability_for_target')) {

        // Group shell + header band (same pattern as the limitation condition sections; primary accent for "target").
        html += '<div class="border border-primary rounded overflow-hidden my-2" role="group" aria-label="Target — Charges / Resources">'
            + '<div class="px-3 py-2 bg-primary text-white border-bottom border-primary"><div class="fw-bold small">'
            + iconMap('target') + ' Target — Charges / Resources</div></div>'
            + '<div class="p-2">';

        const tConsQ = (hasQty('consume_ability_charges_qty_for_target') ? ' ' + qtyBadge('consume_ability_charges_qty_for_target') : '');
        const tConsS = (hasQty('consume_ability_charges_qty_for_target') ? 's' : '');
        html += opBlock('consume_ability_charges_for_target', 'list-group-item-danger', 'Consumes' + tConsQ + ' ' + iconMap('target') + ' Target Ability Charge' + tConsS + ':');

        const tAddQ = (hasQty('add_charges_to_ability_qty_for_target') ? ' ' + qtyBadge('add_charges_to_ability_qty_for_target') : '');
        const tAddS = (hasQty('add_charges_to_ability_qty_for_target') ? 's' : '');
        html += opBlock('add_charges_to_ability_for_target', 'list-group-item-dark', 'Add' + tAddQ + ' Charge' + tAddS + ' to ' + iconMap('target') + ' Target Ability:');

        const tMbQ = (hasQty('maintained_by_ability_qty_for_target') ? ' ' + qtyBadge('maintained_by_ability_qty_for_target') : '');
        const tMbS = (hasQty('maintained_by_ability_qty_for_target') ? 's' : '');
        html += opBlock('maintained_by_ability_for_target', 'list-group-item-dark', 'Maintained by' + tMbQ + ' Charge' + tMbS + ' from ' + iconMap('target') + ' Target Ability, per round:');

        const tIncS = hasLayer(arr, 'increase_max_charges_of_ability_qty_for_target') ? 's by ' + qtyBadge('increase_max_charges_of_ability_qty_for_target') : '';
        html += opBlock('increase_max_charges_of_ability_for_target', 'list-group-item-dark', 'Increases Max Charge' + tIncS + ' of ' + iconMap('target') + ' Target Ability:');

        const tDecS = hasLayer(arr, 'decrease_max_charges_of_ability_qty_for_target') ? 's by ' + qtyBadge('decrease_max_charges_of_ability_qty_for_target') : '';
        html += opBlock('decrease_max_charges_of_ability_for_target', 'list-group-item-dark', 'Decreases Max Charge' + tDecS + ' of ' + iconMap('target') + ' Target Ability:');

        html += '</div></div>';
    }

    return html;
}

// options/list_display/list_display_item_abilities_inc.php (read path)
export function renderItemAbilitiesSubinclude(ctx: RenderCtx): string {
    const arr = ctx.arr;
    let html = '';
    // Item Charges. `current_item_charges` is the per-instance charge state — it sits directly on the
    // inventory instance and is the reliable signal that this IS a charged item. The MAX (`item_charges`)
    // and `item_rechargeable` live on the TEMPLATE def. Resolve the def ROBUSTLY from the catalog by
    // inventory_item_id (ftFindFullNestedItem walks every root catalog) rather than leaning on layerOf's
    // thinned-instance heuristic, which requires the instance's item_layer_types to be ALL-volatile — a
    // single residual non-volatile layer (e.g. equipment_slot on an equipped wand) makes it return
    // undefined, which was hiding the whole block in the side-info even though the def carries item_charges.
    const curCharges = layerOf(arr, 'current_item_charges');
    let maxCharges: unknown = layerOf(arr, 'item_charges');
    let chargeTplDef: unknown = null;
    if (maxCharges === undefined || maxCharges === null) {
        const tplId = String(inventoryField(arr, 'inventory_item_id') ?? arr?.inventory?.inventory_item_id ?? '');
        const finder = window.ftFindFullNestedItem;
        chargeTplDef = (tplId !== '' && typeof finder === 'function') ? finder(tplId) : null;
        if (chargeTplDef) maxCharges = layerOf(chargeTplDef, 'item_charges');
    }
    const hasMax = maxCharges !== undefined && maxCharges !== null && maxCharges !== '' && maxCharges !== 0 && maxCharges !== '0' && maxCharges !== false;
    const hasCur = curCharges !== undefined && curCharges !== null;
    if (hasMax || hasCur) {
        const maxStr = escapeHtml(slotString(maxCharges ?? ''));
        html += '<div class="card-text ft-ld-block"><ul class="list-group border">'
            + '<li class="list-group-item list-group-item-light d-flex align-items-center justify-content-start">'
            + '<div class="me-2">' + iconMap('item_charges') + ' Item Charges:</div>';
        if (hasCur) {
            // inventory_id lives in the `inventory` bucket on a reshaped/cache blob; fall back to the
            // legacy top-level key (PHP reads inventory_field($item_arr, 'inventory_id')).
            const invId = String(inventoryField(arr, 'inventory_id') ?? arr?.inventory_id ?? '');
            const inputId = invId + '_inventory_current_item_charges';
            html += '<div><input id="' + escapeHtml(inputId) + '" class="form-control form-control-sm" type="number" min="0" max="' + maxStr + '" value="' + escapeHtml(slotString(curCharges ?? '')) + '" ' + ldAttrs('change-inventory-charges', { inventoryId: invId, inputId }) + '></div><div class="px-2 h6">/</div>';
        }
        html += '<div> <span class="badge bg-dark h6">' + maxStr + '</span></div>';
        const rechargeable = layerOf(arr, 'item_rechargeable') ?? (chargeTplDef ? layerOf(chargeTplDef, 'item_rechargeable') : undefined);
        if (rechargeable !== undefined && rechargeable !== null) html += '<div><span class="badge bg-dark border">' + iconMap('item_rechargeable') + '</span></div>';
        html += '</li></ul></div>';
    }
    // Enchantment-granted spells/abilities are intentionally derived into
    // computed.enchantment.abilities rather than mutating the pristine authored layer bucket.
    // Feed those rows into the same card-list renderer so spell scrolls and charged items identify
    // what they contain in SideInfo. Authored entries remain first and the renderer dedupes by id.
    const enchantmentAbilities = ctx.arr.computed?.enchantment?.abilities;
    const chargedSpells = Array.isArray(enchantmentAbilities?.charged_spell)
        ? enchantmentAbilities.charged_spell
        : [];
    const chargedAbilities = Array.isArray(enchantmentAbilities?.charged_ability)
        ? enchantmentAbilities.charged_ability
        : [];
    html += renderCardListBlock(ctx, 'charged_spell', {
        label: 'Charged Spell',
        headerClass: 'list-group-item-info fw-bold',
        derivedEntries: chargedSpells,
    });
    html += renderCardListBlock(ctx, 'charged_ability', {
        label: 'Charged Ability',
        headerClass: 'list-group-item-primary fw-bold',
        derivedEntries: chargedAbilities,
    });
    // Trap payloads share the enchantment-derived list with charged abilities.
    const trapAbilities = Array.isArray(enchantmentAbilities?.trap_ability)
        ? enchantmentAbilities.trap_ability
        : [];
    html += renderCardListBlock(ctx, 'trap_ability', {
        label: 'Trap Payload',
        headerClass: 'list-group-item-danger fw-bold',
        derivedEntries: trapAbilities,
    });
    return html;
}

// options/list_display/list_display_feats_inc.php (read path — dropdown FORM branch deferred)
export function renderFeatsSubinclude(ctx: RenderCtx): string {
    const arr = ctx.arr;
    if (!(hasLayer(arr, 'feat') || hasLayer(arr, 'user_choice_1_feat') || hasLayer(arr, 'user_choice_2_feat') || hasLayer(arr, 'user_choice_3_feat') || hasLayer(arr, 'user_choice_4_feat'))) return '';

    let html = '<div class="card-text ft-ld-block"><ul class="list-group border">'
        + '<li class="list-group-item list-group-item-success fw-bold" aria-current="true">' + iconMap('feat') + ' Feat(s):</li>';
    html += renderCardListItemLayerRead(ctx, 'feat', layerEntriesOf(arr, 'feat'));
    // read mode: show ONLY the 1st user-choice select, numbered by the highest present slot
    if (hasLayer(arr, 'user_choice_1_feat')) {
        let num = '1';
        if (hasLayer(arr, 'user_choice_2_feat')) num = '2';
        if (hasLayer(arr, 'user_choice_3_feat')) num = '3';
        if (hasLayer(arr, 'user_choice_4_feat')) num = '4';
        const slot = arr.item_layer_types?.['user_choice_1_feat'] ?? arr['user_choice_1_feat']; // bucket-first
        html += renderUserChoiceReadRow(ctx, Array.isArray(slot) ? slot : [slot], 'feat', num);
    }
    return html + '</ul></div>';
}

// options/list_display/armor/list_display_armor_inc.php (bucket-aware) + crit_to_hit/hit_to_crit rows
export function renderArmorSubinclude(ctx: RenderCtx): string {
    const arr = ctx.arr;
    const hasArmorType = hasLayer(arr, 'armor_type');
    const hasArmorClass = hasLayer(arr, 'armor_classification');
    const hasAcMod = hasLayer(arr, 'ac_mod');
    const showFull = hasArmorType || hasArmorClass;
    const showLone = !showFull && hasAcMod;

    let html = '';
    if (showFull) {
        let acModDisplay = '';
        if (hasAcMod) {
            const raw = layerOf<string | number>(arr, 'ac_mod');
            acModDisplay = (raw !== undefined && raw !== null && raw !== '' && !isNaN(Number(raw))) ? String(raw) : nameOf(ctx.names, String(raw));
        }
        html += '<div class="card-text ft-ld-block"><ul class="list-group border">'
            + '<li class="list-group-item list-group-item-info fw-bold" aria-current="true">' + iconMap('armor') + ' Armor Info</li>';
        if (hasArmorType) {
            html += '<li class="list-group-item"><span class="fw-bold">' + iconMap('armor_type') + ':</span> ' + referenceNameHtml(ctx.names, String(layerOf(arr, 'armor_type') ?? '')) + '</li>';
        }
        if (hasArmorClass) {
            const cid = String(layerOf(arr, 'armor_classification') ?? '');
            html += '<li class="list-group-item"><span class="fw-bold">' + iconMap('armor_classification') + ':</span> <span class="ms-1">' + iconMap(cid) + '</span> <a class="link-primary" href="' + BASE_URL + 'item.php?id=' + escapeHtml(cid) + '">' + referenceNameHtml(ctx.names, cid) + '</a></li>';
        }
        if (hasAcMod) html += '<li class="list-group-item"><span class="fw-bold">Base AC:</span> <span class="badge bg-dark border border-light">+' + escapeHtml(acModDisplay) + '</span></li>';
        if (hasLayer(arr, 'ac_mod_magical')) html += '<li class="list-group-item"><span class="fw-bold">Magical AC:</span> <span class="badge bg-dark border border-light">' + escapeHtml(slotString(layerOf(arr, 'ac_mod_magical') ?? '')) + '</span></li>';
        if (hasLayer(arr, 'ac_mod_magical_vs_ranged_attacks')) html += '<li class="list-group-item"><span class="fw-bold">Magical AC vs Ranged Attacks:</span> <span class="badge bg-dark border border-light">+' + escapeHtml(slotString(layerOf(arr, 'ac_mod_magical_vs_ranged_attacks') ?? '')) + '</span></li>';
        if (hasLayer(arr, 'max_body_ac_mod')) html += '<li class="list-group-item"><span class="fw-bold">Max ' + iconMap('body') + ' AC from this Armor:</span> <span class="badge bg-dark border border-light">+' + escapeHtml(slotString(layerOf(arr, 'max_body_ac_mod') ?? '')) + '</span></li>';
        if (hasLayer(arr, 'requires_body_to_equip')) html += '<li class="list-group-item"><span class="fw-bold">Requires ' + iconMap('body') + ' to Equip:</span> <span class="badge bg-dark border border-light">' + escapeHtml(slotString(layerOf(arr, 'requires_body_to_equip') ?? '')) + '</span></li>';
        html += '</ul></div>';
    } else if (showLone) {
        html += '<ul class="list-group border"><li class="list-group-item"><span class="fw-bold">AC Modifier:</span> +' + escapeHtml(slotString(layerOf(arr, 'ac_mod') ?? '')) + '</li></ul>';
    }

    html += renderSingleItem(ctx, 'crit_to_hit', { contentType: 'label_only', label: 'Critical Hit Becomes Regular Hit', liClass: 'list-group-item-success fw-bold' });
    html += renderSingleItem(ctx, 'hit_to_crit', { contentType: 'label_only', label: 'Any Regular Hit Becomes a Critical Hit', liClass: 'list-group-item-success fw-bold' });
    return html;
}

// options/list_display/list_display_creature_template_vitality_inc.php
export function renderCreatureTemplateVitalitySubinclude(ctx: RenderCtx): string {
    const arr = ctx.arr;
    if (String(itemsCol<string>(arr, 'item_type') ?? '') !== 'creature_template') return '';
    const vLayers = ['v_dice', 'v_die', 'v_mod', 'v_current', 'v_max', 'v_temp', 'v_max_temp'];
    let rows = '';
    for (const k of vLayers) {
        // Rerouted vitality fields are authoritative in computed.vitality. The
        // dice overrides are own layers; inherited carrier values do not belong here.
        const slot = (['v_current', 'v_max', 'v_temp', 'v_max_temp'].includes(k) ? computedValue(arr, 'vitality', k) : undefined)
            ?? layerSlot(arr, k);
        if (slot === undefined || slot === null) continue;
        const slots = Array.isArray(slot) ? slot : [slot];
        const parts: string[] = [];
        for (const raw of slots) {
            const one = (raw && typeof raw === 'object') ? (slotString(raw)) : (raw === null || raw === false ? '' : String(raw));
            if (one !== '') parts.push(one);
        }
        if (parts.length) rows += '<li class="list-group-item d-flex align-items-center flex-wrap gap-2"><span class="fw-bold">' + iconMap(k) + ' ' + escapeHtml(ucwordsAndSpaceTs(k)) + ':</span> <span class="badge bg-dark border">' + escapeHtml(parts.join(', ')) + '</span></li>';
    }
    return rows ? '<div class="card-text ft-ld-block"><ul class="list-group border">'
        + '<li class="list-group-item list-group-item-danger fw-bold" aria-current="true">' + iconMap('v_max') + ' Vitality (Template Overrides)</li>' + rows + '</ul></div>' : '';
}

// options/list_display/list_display_default_locations_inc.php (top-level isset gates + [0] reads)
export function renderDefaultLocationsSubinclude(ctx: RenderCtx): string {
    const arr = ctx.arr;
    const keys = ['default_environment', 'default_location_inside', 'default_min_population', 'default_encounter_amb_id', 'default_encounter_amb_night_id', 'default_encounter_amb_weather_id'];
    if (!keys.some(k => hasLayer(arr, k))) return '';

    let html = '<div class="card-text ft-ld-block"><ul class="list-group border">'
        + '<li class="list-group-item list-group-item-success fw-bold" aria-current="true">' + iconMap('default_location_settings') + ' Default Location Settings:</li>';

    if (hasTopLevel(arr, 'default_environment')) {
        html += '<li class="list-group-item list-group-item">' + iconMap('default_environment') + ' ' + escapeHtml(ucwordsAndSpaceTs('default_environment')) + ': ' + iconMap(String(topSlot0(arr, 'default_environment'))) + '</li>';
    }
    if (hasTopLevel(arr, 'default_location_inside')) {
        html += '<li class="list-group-item list-group-item">' + iconMap('default_location_inside') + ' The location is considered inside by default.</li>';
    }
    if (hasTopLevel(arr, 'default_min_population')) {
        html += '<li class="list-group-item list-group-item">' + iconMap('default_min_population') + ' ' + escapeHtml(ucwordsAndSpaceTs('default_min_population')) + ": <span class='badge bg-dark border border-light'>" + escapeHtml(String(topSlot0(arr, 'default_min_population'))) + '</span></li>';
    }
    const ambBtn = (key: string, iconKey: string) => {
        const id = String(topSlot0(arr, key));
        return '<li class="list-group-item list-group-item">' + iconMap(iconKey) + ': '
            + '<button class="btn btn-warning border border-dark" ' + ldAttrs('play-soundboard', { audioId: id }) + '>' + iconMap(iconKey) + '</button></li>';
    };
    if (hasTopLevel(arr, 'default_encounter_amb_id')) html += ambBtn('default_encounter_amb_id', 'default_encounter_amb');
    if (hasTopLevel(arr, 'default_encounter_amb_night_id')) html += ambBtn('default_encounter_amb_night_id', 'default_encounter_amb_night');
    if (hasTopLevel(arr, 'default_encounter_amb_weather_id')) html += ambBtn('default_encounter_amb_weather_id', 'default_encounter_amb_weather');

    return html + '</ul></div>';
}

// options/list_display/list_display_object_item_composition_properties_inc.php
export function renderObjectCompositionSubinclude(ctx: RenderCtx): string {
    const arr = ctx.arr;
    const T = String(itemsCol<string>(arr, 'item_type') ?? '');
    const vMax = computedValue<number | string>(arr, 'vitality', 'v_max');
    const hasVMax = vMax !== undefined && vMax !== null;

    // (type != creature || type != player_character) is always true; reduces to hasVMax && type!==''
    const vMaxGate = hasVMax && T !== '';
    // Carrier-aware gate (hasLayer, not host-only hasObjLayer): a base weapon's item_composition /
    // object_* layers live on its *_classification carrier (host-wins nest-and-walk), so the section
    // must render when the carrier provides them — the composition loop below already walks the carrier.
    const layerGate = hasLayer(arr, 'object_damage_type_resistance') || hasLayer(arr, 'object_damage_type_immunity')
        || hasLayer(arr, 'object_damage_type_vulnerability') || hasLayer(arr, 'object_vitality_multiplier')
        || hasLayer(arr, 'object_damage_mitigation') || hasLayer(arr, 'item_composition');
    if (!vMaxGate && !layerGate) return '';

    const itemId = String(itemsCol<string>(arr, 'item_id') ?? '');
    const invId = String(inventoryField(arr, 'inventory_id') ?? '');
    const sanitize = (s: string) => s.replace(/[^a-zA-Z0-9_-]/g, '_');
    let collapseId: string;
    if (invId) collapseId = 'object_properties_collapse_' + sanitize(invId);
    else if (itemId) collapseId = 'object_properties_collapse_item_' + sanitize(itemId);
    else collapseId = 'object_properties_collapse_x_' + sanitize(Object.keys(arr ?? {}).join('').slice(0, 14));

    let html = '<div class="list-display-object-properties-wrap mb-2 rounded overflow-hidden border border-secondary-subtle">'
        + '<button type="button" class="object-properties-header-toggle list-group-item list-group-item-dark list-group-item-action fw-bold w-100 d-flex align-items-center justify-content-between gap-2 rounded-0 border-0 text-start" data-bs-toggle="collapse" data-bs-target="#' + escapeHtml(collapseId) + '" aria-expanded="false" aria-controls="' + escapeHtml(collapseId) + '" title="Show or hide object properties">'
        + '<span class="text-truncate">Object Properties</span>'
        + '<svg class="object-properties-chevron flex-shrink-0" width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/></svg>'
        + '<span class="visually-hidden">Toggle object properties</span></button>'
        + '<div class="collapse px-2 py-2" id="' + escapeHtml(collapseId) + '">';

    if (vMaxGate) {
        const vCurrent = computedValue<number | string>(arr, 'vitality', 'v_current');
        const vMaxVal = computedValue<number | string>(arr, 'vitality', 'v_max');
        html += '<div class="card-text ft-ld-block small mb-2"><ul class="list-group border">';
        if (vCurrent !== null && vCurrent !== undefined) html += '<li class="list-group-item list-group-item-primary fw-bold py-2" aria-current="true">' + iconMap('v_current') + ' V Current: <span class="badge bg-dark border">' + escapeHtml(vCurrent) + '</span></li>';
        if (vMaxVal !== null && vMaxVal !== undefined) html += '<li class="list-group-item list-group-item-secondary fw-bold py-2" aria-current="true">' + iconMap('v_max') + ' V Max: <span class="badge bg-dark border">' + escapeHtml(vMaxVal) + '</span></li>';
        html += '</ul></div>';
    }

    html += renderCardListBlock(ctx, 'object_damage_type_resistance', { label: 'Object Damage Type Resistances', headerClass: 'list-group-item-success border fw-bold' });
    html += renderCardListBlock(ctx, 'object_damage_type_immunity', { label: 'Object Damage Type Immunity', headerClass: 'list-group-item-success border fw-bold' });
    html += renderCardListBlock(ctx, 'object_damage_type_vulnerability', { label: 'Object Damage Type Vulnerability', headerClass: 'list-group-item-dark border fw-bold' });
    html += renderSingleItem(ctx, 'object_vitality_multiplier', { liClass: 'list-group-item border', badgeClass: 'bg-dark border' });
    html += renderSingleItem(ctx, 'object_damage_mitigation', { label: 'Object Damage Mitigation', liClass: 'list-group-item border', badgeClass: 'bg-dark border' });

    for (const compRef of layerEntriesOf(arr, 'item_composition')) {
        // Stage 3 catalog-as-store: a composition entry is now a bare-UUID ref. resolveNest
        // resolves it to the full def via fullNodeById, which indexes root.catalog values
        // (full nests) — an in-blob lookup, no fetch. Full nests pass straight through.
        const comp = resolveNest(compRef, arr);
        if (!comp || typeof comp !== 'object') continue;
        const cctx: RenderCtx = { arr: comp, names: ctx.names };
        html += renderCardListBlock(cctx, 'object_damage_type_resistance', { label: 'Object Damage Type Resistances', headerClass: 'list-group-item-success border fw-bold' });
        html += renderCardListBlock(cctx, 'object_damage_type_immunity', { label: 'Object Damage Type Immunity', headerClass: 'list-group-item-success border fw-bold' });
        html += renderCardListBlock(cctx, 'object_damage_type_vulnerability', { label: 'Object Damage Type Vulnerability', headerClass: 'list-group-item-dark border fw-bold' });
        html += renderSingleItem(cctx, 'object_vitality_multiplier', { liClass: 'list-group-item border', badgeClass: 'bg-dark border' });
        html += renderSingleItem(cctx, 'object_damage_mitigation', { label: 'Object Damage Mitigation', liClass: 'list-group-item border', badgeClass: 'bg-dark border' });
    }

    html += '</div></div>'
        + '<style>.list-display-object-properties-wrap .object-properties-chevron { transition: transform 0.2s ease; } .list-display-object-properties-wrap .object-properties-header-toggle[aria-expanded="false"] .object-properties-chevron { transform: rotate(-90deg); }</style>';
    return html;
}

// options/list_display/list_display_weapon_inc.php
export function renderWeaponSubinclude(ctx: RenderCtx): string {
    const arr = ctx.arr;
    if (!(hasLayer(arr, 'weapon_type') || hasLayer(arr, 'weapon_classification') || hasLayer(arr, 'weapon_category'))) {
        return '';
    }
    const rows: string[] = ['<li class="list-group-item list-group-item-info fw-bold" aria-current="true">' + iconMap('weapon') + ' Weapon:</li>'];

    if (hasLayer(arr, 'weapon_category')) {
        rows.push('<li class="list-group-item"><span class="fw-bold">Category:</span> ' + referenceNameHtml(ctx.names, layerOf<string>(arr, 'weapon_category') ?? '') + '</li>');
    }
    if (hasLayer(arr, 'weapon_type')) {
        const id = layerOf<string>(arr, 'weapon_type') ?? '';
        rows.push('<li class="list-group-item"><span class="fw-bold">Type:</span> <a class="link-primary" href="' + BASE_URL + 'item.php?id=' + escapeHtml(id) + '">' + iconMap(id) + ' ' + referenceNameHtml(ctx.names, id) + '</a></li>');
    }
    if (hasLayer(arr, 'weapon_classification')) {
        const id = layerOf<string>(arr, 'weapon_classification') ?? '';
        rows.push('<li class="list-group-item"><span class="fw-bold">Classification:</span> <a class="link-primary" href="' + BASE_URL + 'item.php?id=' + escapeHtml(id) + '">' + iconMap(id) + '</a></li>');
    }
    // WEAPON REACH IS A DELTA, NOT AN ABSOLUTE (owner 2026-08-15). The engine adds
    // max(0, weapon_reach - 5) to the wielder's reach (is_target_in_range.php:127), so the number
    // that matters to a player is how much the weapon EXTENDS them. Printing the raw absolute made
    // a 10 ft whip look like it had shorter reach than the wielder already has, and printed a
    // noise "Reach: 5" on the 32 items that author the default. Show the extension or nothing.
    if (hasLayer(arr, 'weapon_reach')) {
        const reachExt = Number(slotString(layerOf(arr, 'weapon_reach') ?? '')) - 5;
        if (Number.isFinite(reachExt) && reachExt > 0) {
            rows.push('<li class="list-group-item"><span class="fw-bold">Weapon Reach:</span> +' + escapeHtml(String(reachExt)) + ' ft.</li>');
        }
    }
    if (hasLayer(arr, 'range_begin')) {
        const begin = slotString(layerOf(arr, 'range_begin') ?? '');
        const end = slotString(layerOf(arr, 'range_end') ?? '');
        rows.push('<li class="list-group-item"><span class="fw-bold">Range:</span> ' + escapeHtml(begin) + ' ' + (end !== begin ? '(' + escapeHtml(end) + ')' : '') + '</li>');
    }
    if (hasLayer(arr, 'uses_ammo_classification')) {
        const id = layerOf<string>(arr, 'uses_ammo_classification') ?? '';
        rows.push('<li class="list-group-item"><span class="fw-bold">Ammo:</span> <a class="link-primary" href="' + BASE_URL + 'item.php?id=' + escapeHtml(id) + '">' + referenceNameHtml(ctx.names, id) + '</a></li>');
    }
    if (hasLayer(arr, 'weapon_attack_mod')) {
        const v = Number(slotString(layerOf(arr, 'weapon_attack_mod') ?? ''));
        rows.push('<li class="list-group-item"><span class="fw-bold">Attack Roll Modifier:</span> ' + (v > 0 ? '+' : '') + v + '</li>');
    }
    // EXTRA PROPERTIES
    if (hasLayer(arr, 'uses_ammo_classification')) {
        rows.push('<li class="list-group-item"><div class="d-inline-flex badge bg-secondary mr-2 p-2">Ammo</div> <div>You can use a weapon that has the Ammunition property to make a <a class="link-primary" href="' + BASE_URL + 'item.php?id=3F85CA97-F9B2-A83A-FDE8-E523FD99346C">Weapon Ranged Attack Roll</a> only if you have Ammunition to fire from the weapon. Each time you Attack with the weapon, you expend one piece of Ammunition. Drawing the Ammunition from a Quiver, case, or other container is part of the Attack (you need a free hand to load a one-handed weapon).</div></li>');
    }
    if (hasLayer(arr, 'weapon_heavy')) {
        rows.push('<li class="list-group-item"><div class="d-inline-flex badge bg-secondary mr-2 p-2">Heavy</div><div>You must have a <a class="link-primary" href="' + BASE_URL + 'item.php?id=22918408-67CC-4093-80E8-BF6FA8915C21">Body</a> of at least 2 to be equipped.</div></li>');
    }
    if (hasLayer(arr, 'weapon_light')) {
        rows.push('<li class="list-group-item"><div class="d-inline-flex badge bg-secondary mr-2 p-2">Light</div><div>A light weapon is small and easy to handle, making it ideal for use when fighting with Duel Weapons.</div></li>');
    }
    if (hasLayer(arr, 'reach')) {
        rows.push('<li class="list-group-item"><div class="d-inline-flex badge bg-secondary mr-2 p-2">Reach</div><div>This weapon adds 5 feet to your reach when you Attack with it, as well as when determining your reach for Opportunity Attacks with it.</div></li>');
    }
    if (hasLayer(arr, 'item_throwable')) {
        rows.push('<li class="list-group-item"><div class="d-inline-flex badge bg-secondary mr-2 p-2">Throwable</div><div>If a weapon has the thrown property, you can throw the weapon to make a <a class="link-primary" href="' + BASE_URL + 'item.php?id=3F85CA97-F9B2-A83A-FDE8-E523FD99346C">Weapon Ranged Attack Roll</a>.  The <a class="link-primary" href="' + BASE_URL + 'item.php?id=82BBB3B6-4646-48E8-A5A2-8DC0947A91D0">Damage Roll</a> always adds your <a class="link-primary" href="' + BASE_URL + 'item.php?id=22918408-67CC-4093-80E8-BF6FA8915C21">Body</a> Modifier for Throwable weapons.</div></li>');
    }
    if (hasLayer(arr, 'weapon_versatile') && Number(layerOf(arr, 'weapon_versatile')) === 1) {
        rows.push('<li class="list-group-item"><div class="d-inline-flex badge bg-secondary mr-2 p-2">Versatile</div><div>This weapon can be used with one or two hands. A damage value in parentheses appears with the property—the damage when the weapon is used with two hands to make a melee Attack.</div></li>');
    }

    return '<div class="card-text ft-ld-block"><ul class="list-group border">' + rows.join('') + '</ul></div>';
}

// Ammunition references can be bare UUIDs, shells or complete nested definitions.
export function renderAmmoSubinclude(ctx: RenderCtx): string {
    const arr = ctx.arr;
    if (!hasLayer(arr, 'weapon_ammo_classification')) return '';
    const rows: string[] = [
        '<li class="list-group-item list-group-item-info fw-bold" aria-current="true">' + iconMap('weapon_ammo_classification') + ' Ammunition:</li>',
    ];

    const cls = scalarLayerSlot(layerOf(arr, 'weapon_ammo_classification'));
    rows.push('<li class="list-group-item"><span class="fw-bold">Classification:</span> <a class="link-primary" href="' + BASE_URL + 'item.php?id=' + escapeHtml(cls) + '">' + referenceNameHtml(ctx.names, cls) + '</a></li>');

    if (hasLayer(arr, 'weapon_attack_mod')) {
        const v = Number(slotString(layerOf(arr, 'weapon_attack_mod') ?? ''));
        rows.push('<li class="list-group-item"><span class="fw-bold">Attack Roll Modifier:</span> ' + (v > 0 ? '+' : '') + v + '</li>');
    }
    if (hasLayer(arr, 'damage_mod')) {
        const v = Number(slotString(layerOf(arr, 'damage_mod') ?? ''));
        rows.push('<li class="list-group-item"><span class="fw-bold">Damage Roll Modifier:</span> ' + (v > 0 ? '+' : '') + v + '</li>');
    }
    if (hasLayer(arr, 'crit_on_hit')) {
        rows.push('<li class="list-group-item"><div class="d-inline-flex badge bg-secondary mr-2 p-2">Crit on Hit</div><div>Any hit becomes an automatic critical hit.</div></li>');
    }

    return '<div class="card-text ft-ld-block"><ul class="list-group border">' + rows.join('') + '</ul></div>';
}

// options/list_display/list_display_max_item_charges_inc.php — gate !empty($arr['max_item_charges'][0])
export function renderMaxItemChargesSubinclude(ctx: RenderCtx): string {
    const arr = ctx.arr;
    const gate = topSlot0(arr, 'max_item_charges');
    if (gate === '' || gate === null || gate === undefined || gate === 0 || gate === '0' || gate === false) return '';

    let html = '<div class="card-text ft-ld-block"><ul class="list-group border">'
        + '<li class="list-group-item list-group-item-success border fw-bold" aria-current="true">' + iconMap('max_item_charges') + ' Item Charges:</li>'
        + '<li class="list-group-item"><span class="fw-bold">Max item charges:</span> <span class="badge bg-dark">' + escapeHtml(slotString(layerOf(arr, 'max_item_charges') ?? '')) + '</span></li>';

    if (hasTopLevel(arr, 'item_recharges_dice') || hasTopLevel(arr, 'item_recharges_time')) {
        html += '<li class="list-group-item"><span class="fw-bold">Recharges:</span> ';
        if (hasTopLevel(arr, 'item_recharges_dice')) {
            html += '<span class="badge bg-dark">' + escapeHtml(slotString(layerOf(arr, 'item_recharges_dice') ?? '')) + 'd' + escapeHtml(slotString(layerOf(arr, 'item_recharges_die') ?? '')) + ' ';
            if (hasTopLevel(arr, 'item_recharges_mod')) {
                html += '+ ' + escapeHtml(String(topSlot0(arr, 'item_recharges_mod')));
            }
            html += '</span> /';
        }
        if (hasTopLevel(arr, 'item_recharges_time')) {
            html += '<span class="badge bg-dark">' + escapeHtml(ucfirst(String(topSlot0(arr, 'item_recharges_time')))) + '</span>';
        }
        html += '</li>';
    }

    html += '</ul></div>';
    return html;
}

export const propertySectionRenderers = {
    essences: renderEssencesSubinclude, range: renderRangeAoeSubinclude, spell: renderSpellInfoSubinclude,
    armor: renderArmorSubinclude, weapon: renderWeaponSubinclude, ammo: renderAmmoSubinclude,
    ancestry: renderAncestryInfoSubinclude, composition: renderObjectCompositionSubinclude,
    rolls: renderRollTypesSubinclude, resets: renderAbilityResetsSubinclude, innate: renderInnateSpellsSubinclude,
    'item-abilities': renderItemAbilitiesSubinclude, vitality: renderCreatureTemplateVitalitySubinclude,
};
installPropertyFormMounts(propertySectionRenderers);
